import React from 'react';
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Button from "@amzn/meridian/button";
import Tooltip from "@amzn/meridian/tooltip";
import Tag from "@amzn/meridian/tag";
import infoTokens from "@amzn/meridian-tokens/base/icon/info";
import { PrimaryGradingVolumeData, EnhancedShiftTotals } from "../../../pages/reviewAndPublish/model";
import {
  TOOLTIP_V2_RCC_RECOMMENDED_VOLUME,
  TOOLTIP_V2_OPERATOR_OVERRIDE_VOLUME,
  TOOLTIP_V2_ALPS_PLANNED_VOLUME,
  TOOLTIP_V2_LABOR_HOURS_DELTA,
} from "../../../i18n/tooltips";

export interface TopMetricsRowStateProps {
  primaryGradingVolumeData?: PrimaryGradingVolumeData;
  /** RCC Recommended Volume = total of all units in the chart (peak cumulative). */
  recommendedVolumeTotal?: number;
  shiftTotals?: EnhancedShiftTotals;
  /** Labor-hours delta as a percentage (drives the deficit/surplus pill). */
  laborHoursDeltaPercent?: number;
  /** True for the RCC Proposed view, false for Operator Override — drives the
   *  volume card label ("RCC Recommended Volume" vs "RCC Recommended Volume - Overridden").
   *  Defaults true so existing (RCC-view) usages/tests are unaffected. */
  isRccProposedView?: boolean;
}

const numberFormatter = new Intl.NumberFormat('en-US');

const formatValue = (value?: number): string =>
  value === undefined || value === null ? '--' : numberFormatter.format(value);

// Labor-hours delta percentage is shown to 1 decimal place (per the mock), e.g.
// 15.89085137215451 -> "15.9".
const formatPercent = (value: number): string => Math.abs(value).toFixed(1);

/**
 * Refactored (V2) top metrics row — UTR-REFLEX-1163.
 *
 * Three white cards matching the mock: RCC Recommended Volume, ALPS Planned
 * Volume, and Labor Hours Delta (with a deficit/surplus pill). The "Overridden
 * Primary Grading Volume" card from V1 is intentionally dropped. Reuses the
 * existing shiftPlanOutput fields; only laborHoursDeltaPercent is new
 * (UTR-REFLEX-1172). Kept as a sibling so V1's TopMetricsRow is untouched.
 */
const TopMetricsRow: React.FC<TopMetricsRowStateProps> = ({
  primaryGradingVolumeData,
  recommendedVolumeTotal,
  shiftTotals,
  laborHoursDeltaPercent,
  isRccProposedView = true,
}) => {
  // Volume card label AND tooltip follow the selected plan view: the curve/total
  // belongs to whichever plan is being viewed (Operator Override plan → its own
  // volume, not RCC recommended). Selected together so the heading, info-icon
  // title, and tooltip body stay in sync. Mirrors the PDF export's volume heading.
  const volumeLabel = isRccProposedView ? 'RCC Recommended Volume' : 'RCC Recommended Volume - Overridden';
  const volumeTooltip = isRccProposedView
    ? TOOLTIP_V2_RCC_RECOMMENDED_VOLUME
    : TOOLTIP_V2_OPERATOR_OVERRIDE_VOLUME;

  // RCC Recommended Volume is the total of all units in the chart; fall back to
  // primaryGradingVolume.recommended.total if the projected series is absent.
  const recommendedTotal = recommendedVolumeTotal ?? primaryGradingVolumeData?.recommended?.total;
  const alpsPlanned = primaryGradingVolumeData?.recommended?.alps;

  // Difference of RCC recommended vs ALPS target (existing derivation). Exactly
  // zero is "on target" — neither surplus nor deficit — so the delta line is
  // hidden, matching how the Labor Hours Delta card treats a balanced value.
  const recommendedDiff =
    recommendedTotal !== undefined && alpsPlanned !== undefined
      ? recommendedTotal - alpsPlanned
      : undefined;
  const showVolumeDelta = recommendedDiff !== undefined && recommendedDiff !== 0;
  const recommendedSurplus = recommendedDiff !== undefined && recommendedDiff > 0;

  // Labor Hours Delta from the backend field showHoursNeeded = "hours still
  // needed" = Required − Available: POSITIVE => more needed than available =>
  // short-staffed (deficit); NEGATIVE => surplus; ZERO => on target (balanced).
  // The value is shown as an UNSIGNED magnitude (product-confirmed); direction is
  // conveyed by the pill + "lower/higher than ALPS requirement" copy, not by a sign.
  const showHoursNeeded = shiftTotals?.showHoursNeeded?.intValue;
  const isDeficit = showHoursNeeded !== undefined && showHoursNeeded > 0;
  const isSurplus = showHoursNeeded !== undefined && showHoursNeeded < 0;
  const laborHoursMagnitude = showHoursNeeded === undefined ? undefined : Math.abs(showHoursNeeded);
  const laborHoursDirection = isDeficit ? 'lower' : isSurplus ? 'higher' : '';

  // Info icon + Meridian Tooltip. Tooltip manages its own hover/focus state and
  // anchors to the icon button (no custom hover state or positioning needed).
  const renderInfoIcon = (tooltip: string, title: string) => (
    <Tooltip position="top" title={tooltip}>
      <Button type="icon" size="small" label={`Info: ${title}`}>
        <Icon tokens={infoTokens} />
      </Button>
    </Tooltip>
  );

  return (
    <Row widths={["fill", "fill", "fill"]} spacing="400" alignmentVertical="stretch">
      {/* RCC Recommended Volume / RCC Recommended Volume - Overridden (per plan view) */}
      <Box type="outline" spacingInset="400">
        <Column spacing="200">
          <Row alignmentVertical="center" spacing="100" widths={["fill", "fit"]}>
            <Text type="h100" color="secondary">{volumeLabel}</Text>
            {renderInfoIcon(volumeTooltip, volumeLabel)}
          </Row>
          <Row alignmentVertical="baseline" spacing="100" className="metric-card-v2__value">
            <Heading level={2} type="h500">{formatValue(recommendedTotal)}</Heading>
            <Text type="h100" color="secondary">units</Text>
          </Row>
          {showVolumeDelta && (
            <Text
              type="h100"
              color={recommendedSurplus ? 'success' : 'error'}
            >
              {`${recommendedSurplus ? '+' : '-'} ${formatValue(Math.abs(recommendedDiff!))} units vs. ALPS target`}
            </Text>
          )}
        </Column>
      </Box>

      {/* ALPS Planned Volume */}
      <Box type="outline" spacingInset="400">
        <Column spacing="200">
          <Row alignmentVertical="center" spacing="100" widths={["fill", "fit"]}>
            <Text type="h100" color="secondary">ALPS Planned Volume</Text>
            {renderInfoIcon(TOOLTIP_V2_ALPS_PLANNED_VOLUME, 'ALPS Planned Volume')}
          </Row>
          <Row alignmentVertical="baseline" spacing="100" className="metric-card-v2__value">
            <Heading level={2} type="h500">{formatValue(alpsPlanned)}</Heading>
            <Text type="h100" color="secondary">units</Text>
          </Row>
        </Column>
      </Box>

      {/* Labor Hours Delta */}
      <Box type="outline" spacingInset="400">
        <Column spacing="200">
          <Row alignmentVertical="center" spacing="100" widths={["fill", "fit"]}>
            <Text type="h100" color="secondary">Labor Hours Delta</Text>
            {renderInfoIcon(TOOLTIP_V2_LABOR_HOURS_DELTA, 'Labor Hours Delta')}
          </Row>
          <Row alignmentVertical="baseline" spacing="100" className="metric-card-v2__value">
            <Heading level={2} type="h500">
              {laborHoursMagnitude === undefined ? '--' : formatValue(laborHoursMagnitude)}
            </Heading>
            <Text type="h100" color="secondary">
              {isDeficit || isSurplus
                ? `labor hours ${laborHoursDirection} than ALPS requirement`
                : 'labor hours vs ALPS'}
            </Text>
          </Row>
          {laborHoursDeltaPercent !== undefined && (isDeficit || isSurplus) && (
            <Box>
              <Tag type={isDeficit ? 'error' : 'success'}>
                {isDeficit
                  ? `Deficit: Short-staffed by ${formatPercent(laborHoursDeltaPercent)}%`
                  : `Surplus: Over-staffed by ${formatPercent(laborHoursDeltaPercent)}%`}
              </Tag>
            </Box>
          )}
        </Column>
      </Box>
    </Row>
  );
};

export default TopMetricsRow;
